import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMessages } from 'dignity.js/react';
import { LOBBY_CHAT_PEER_GROUP, LOBBY_SCOPE } from './node.js';
import {
  CHAT_MESSAGE_TYPE,
  CHAT_ROOM_MESSAGE_TYPE,
  MAIN_CHAT_ROOM_ID,
  defaultChatRooms,
  deliverChatPayload,
  mergeChatMessages,
  mergeChatRooms,
  normalizeRoomId,
  parseIncomingChatMessage,
  parseIncomingChatRoom,
  sealChatMessage,
  sealChatRoom
} from './lobbyChat.js';
import { lobbyDebug, lobbyDebugReport } from './lobbyDebug.js';
import {
  publishLocalLobbyEnvelope,
  subscribeLocalLobbyEnvelope
} from './lobbyLocalBus.js';

const PENDING_CHAT_HELLO_LIMIT = 6;
const MAX_CHAT_CONNECT_TARGETS = 24;
const DIRECTORY_CHAT_LIMIT = 12;

export default function useLobbyChat({
  node,
  joined,
  identity,
  nickname = '',
  openPeerIds = [],
  preferPeerIds = [],
  meshPeerIds = [],
  helloMessages = [],
  basketChats = [],
  basketRooms = [],
  directorySync = null,
  ensureLinkedToPeerIds,
  publishOutOfBand,
  publishRoomOutOfBand,
  onChatSent
} = {}) {
  const [activeRoomId, setActiveRoomId] = useState(MAIN_CHAT_ROOM_ID);
  const [rooms, setRooms] = useState(() => defaultChatRooms());
  const [messagesByRoom, setMessagesByRoom] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const activeRoomIdRef = useRef(activeRoomId);
  const ingestedCountRef = useRef(0);
  const pendingOutboundRef = useRef([]);
  const roomsRef = useRef(rooms);
  const messagesByRoomRef = useRef(messagesByRoom);
  const seenHelloChatKeysRef = useRef(new Set());
  const seenBasketChatKeysRef = useRef(new Set());
  const seenBasketRoomKeysRef = useRef(new Set());
  const peerGroupJoinedRef = useRef(false);
  const lastOpenKeyRef = useRef('');
  activeRoomIdRef.current = activeRoomId;
  roomsRef.current = rooms;
  messagesByRoomRef.current = messagesByRoom;

  const chatFilter = useCallback(
    (message) =>
      message.type === CHAT_MESSAGE_TYPE || message.type === CHAT_ROOM_MESSAGE_TYPE,
    []
  );
  const meshMessages = useMessages(node, chatFilter);

  const connectTargets = useMemo(() => {
    const prefer = [
      ...(openPeerIds || []),
      ...(preferPeerIds || []),
      ...(meshPeerIds || [])
    ].filter(Boolean);
    return prefer.filter((peerId, index, list) => (
      peerId
      && peerId !== identity?.peerId
      && list.indexOf(peerId) === index
    )).slice(0, MAX_CHAT_CONNECT_TARGETS);
  }, [identity?.peerId, meshPeerIds, openPeerIds, preferPeerIds]);

  const appendChat = useCallback((chat) => {
    if (!chat) {
      return;
    }
    setMessagesByRoom((current) => ({
      ...current,
      [chat.roomId]: mergeChatMessages(current[chat.roomId] || [], chat)
    }));
  }, []);

  const appendRoom = useCallback((room) => {
    if (!room?.roomId) {
      return;
    }
    setRooms((current) => mergeChatRooms(current, room));
  }, []);

  const ingestWireMessage = useCallback(async (message) => {
    if (!message) {
      return;
    }
    if (message.type === CHAT_ROOM_MESSAGE_TYPE) {
      let room = await parseIncomingChatRoom(message, {
        requireAntiSpam: Boolean(message.payload?.antiSpam)
      });
      // Signed PeerJS envelopes already authenticate the sender; accept room
      // gossip if the Sloth proof is missing/stale so late joiners still sync.
      if (!room && message.payload) {
        room = await parseIncomingChatRoom(message, { requireAntiSpam: false });
      }
      if (room) {
        appendRoom(room);
      }
      return;
    }
    if (message.type === CHAT_MESSAGE_TYPE) {
      let chat = await parseIncomingChatMessage(message, {
        requireAntiSpam: Boolean(message.payload?.antiSpam)
      });
      if (!chat && message.payload) {
        chat = await parseIncomingChatMessage(message, { requireAntiSpam: false });
      }
      if (chat) {
        lobbyDebug('chat-ingest-webrtc', {
          text: chat.text,
          from: chat.senderId || chat.playerId,
          roomId: chat.roomId
        });
        lobbyDebugReport('CHAT_RECV', {
          role: 'receiver',
          via: 'wire',
          text: chat.text,
          roomId: chat.roomId,
          fromPeerId: chat.senderId || message.senderId,
          fromPlayerId: chat.playerId,
          selfPeerId: identity?.peerId,
          selfPlayerId: identity?.playerId
        });
        appendChat(chat);
      } else {
        lobbyDebug('chat-ingest-webrtc-drop', {
          senderId: message.senderId,
          hasAntiSpam: Boolean(message.payload?.antiSpam),
          payloadKeys: message.payload ? Object.keys(message.payload) : []
        });
        lobbyDebugReport('CHAT_RECV_DROP', {
          role: 'receiver',
          via: 'wire',
          senderId: message.senderId,
          hasAntiSpam: Boolean(message.payload?.antiSpam),
          text: message.payload?.text || '',
          hint: 'Envelope arrived but parseIncomingChatMessage rejected it.'
        });
      }
    }
  }, [appendChat, appendRoom, identity?.peerId, identity?.playerId]);

  useEffect(() => {
    let cancelled = false;
    const ingest = async () => {
      const start = ingestedCountRef.current;
      for (let index = start; index < meshMessages.length; index += 1) {
        const message = meshMessages[index];
        await ingestWireMessage(message);
        if (cancelled) {
          return;
        }
        ingestedCountRef.current = index + 1;
      }
    };
    void ingest();
    return () => {
      cancelled = true;
    };
  }, [ingestWireMessage, meshMessages]);

  useEffect(() => {
    ingestedCountRef.current = 0;
    peerGroupJoinedRef.current = false;
  }, [node]);

  // Same-origin multi-window bus — works when PeerJS/PeerBasket do not.
  useEffect(() => {
    if (!identity?.peerId) {
      return undefined;
    }
    return subscribeLocalLobbyEnvelope((envelope) => {
      if (!envelope || envelope.senderId === identity.peerId) {
        return;
      }
      if (
        envelope.type !== CHAT_MESSAGE_TYPE
        && envelope.type !== CHAT_ROOM_MESSAGE_TYPE
      ) {
        return;
      }
      void ingestWireMessage({
        type: envelope.type,
        senderId: envelope.senderId,
        payload: envelope.payload
      });
    });
  }, [identity?.peerId, ingestWireMessage]);

  // dignity.js PeerGroup gossip — preferred high-fanout path once bootstrap peers exist.
  useEffect(() => {
    if (!node || !joined || typeof node.joinPeerGroup !== 'function') {
      return undefined;
    }
    const targets = connectTargets.slice(0, 8);
    let cancelled = false;

    const join = async () => {
      try {
        await node.joinPeerGroup(LOBBY_CHAT_PEER_GROUP, {
          bootstrapPeerIds: targets,
          fanout: 4,
          maxActivePeers: 12,
          maxHops: 32
        });
        if (!cancelled) {
          peerGroupJoinedRef.current = true;
        }
      } catch {
        peerGroupJoinedRef.current = false;
      }
    };
    void join();

    const onGroupMessage = (event) => {
      if (!event || event.groupId !== LOBBY_CHAT_PEER_GROUP) {
        return;
      }
      void ingestWireMessage({
        type: event.type,
        senderId: event.senderId,
        payload: event.payload
      });
    };
    node.on?.('peergroupmessage', onGroupMessage);

    return () => {
      cancelled = true;
      node.off?.('peergroupmessage', onGroupMessage);
    };
  }, [connectTargets, ingestWireMessage, joined, node]);

  useEffect(() => {
    let cancelled = false;
    const ingest = async () => {
      for (const message of helloMessages || []) {
        const pendingRooms = message?.payload?.pendingRooms;
        const roomBatch = Array.isArray(pendingRooms)
          ? pendingRooms
          : (pendingRooms ? [pendingRooms] : []);
        for (const entry of roomBatch) {
          appendRoom(entry);
        }
        const pending = message?.payload?.pendingChat;
        const batch = Array.isArray(pending) ? pending : (pending ? [pending] : []);
        for (const entry of batch) {
          const synthetic = {
            type: CHAT_MESSAGE_TYPE,
            senderId: message.senderId,
            payload: entry
          };
          const chat = await parseIncomingChatMessage(synthetic, {
            requireAntiSpam: Boolean(entry?.antiSpam)
          });
          if (!chat || cancelled) {
            continue;
          }
          if (seenHelloChatKeysRef.current.has(chat.messageKey)) {
            continue;
          }
          seenHelloChatKeysRef.current.add(chat.messageKey);
          appendChat(chat);
        }
      }
    };
    void ingest();
    return () => {
      cancelled = true;
    };
  }, [appendChat, appendRoom, helloMessages]);

  useEffect(() => {
    for (const entry of basketChats || []) {
      const messageKey = entry?.messageKey
        || `${entry?.senderId || entry?.playerId}:${entry?.sentAt}:${String(entry?.text || '').slice(0, 24)}`;
      if (!entry?.text || !messageKey || seenBasketChatKeysRef.current.has(messageKey)) {
        continue;
      }
      seenBasketChatKeysRef.current.add(messageKey);
      lobbyDebug('chat-ingest-basket', {
        text: entry.text,
        from: entry.senderId || entry.playerId,
        roomId: entry.roomId
      });
      lobbyDebugReport('CHAT_RECV', {
        role: 'receiver',
        via: 'peerbasket',
        text: entry.text,
        roomId: entry.roomId || MAIN_CHAT_ROOM_ID,
        fromPeerId: entry.senderId || '',
        fromPlayerId: entry.playerId,
        selfPeerId: identity?.peerId,
        selfPlayerId: identity?.playerId
      });
      appendChat({
        roomId: entry.roomId || MAIN_CHAT_ROOM_ID,
        text: entry.text,
        nickname: entry.nickname || '',
        playerId: entry.playerId,
        sentAt: entry.sentAt,
        senderId: entry.senderId || entry.playerId,
        receivedAt: Date.now(),
        messageKey
      });
    }
  }, [appendChat, basketChats, identity?.peerId, identity?.playerId]);

  useEffect(() => {
    for (const entry of basketRooms || []) {
      const key = `${entry.roomId}:${entry.createdBy}:${entry.createdAt}`;
      if (!entry?.roomId || seenBasketRoomKeysRef.current.has(key)) {
        continue;
      }
      seenBasketRoomKeysRef.current.add(key);
      appendRoom(entry);
    }
  }, [appendRoom, basketRooms]);

  // Directory gossip carries rooms + recent chat (works whenever any PeerJS link is open).
  useEffect(() => {
    if (!directorySync) {
      return;
    }
    for (const room of directorySync.rooms || []) {
      appendRoom(room);
    }
    for (const entry of directorySync.recentChat || []) {
      if (!entry?.text || !entry?.playerId) {
        continue;
      }
      const senderId = entry.senderId || entry.playerId;
      const messageKey = `${senderId}:${entry.sentAt}:${String(entry.text).slice(0, 24)}`;
      appendChat({
        roomId: entry.roomId || MAIN_CHAT_ROOM_ID,
        text: entry.text,
        nickname: entry.nickname || '',
        playerId: entry.playerId,
        sentAt: entry.sentAt,
        senderId,
        receivedAt: Date.now(),
        messageKey
      });
    }
  }, [appendChat, appendRoom, directorySync]);

  const getChatSyncPayload = useCallback(() => {
    const recentChat = Object.values(messagesByRoomRef.current)
      .flat()
      .sort((left, right) => Number(left.sentAt || 0) - Number(right.sentAt || 0))
      .slice(-DIRECTORY_CHAT_LIMIT)
      .map((entry) => ({
        roomId: entry.roomId,
        text: entry.text,
        nickname: entry.nickname || '',
        playerId: entry.playerId,
        senderId: entry.senderId,
        sentAt: entry.sentAt
      }));
    const syncRooms = (roomsRef.current || [])
      .filter((room) => room.roomId && room.roomId !== MAIN_CHAT_ROOM_ID)
      .map((room) => ({
        roomId: room.roomId,
        title: room.title,
        createdBy: room.createdBy || '',
        createdAt: room.createdAt || 0
      }));
    return { rooms: syncRooms, recentChat };
  }, []);

  const publishToPeerGroup = useCallback(async (type, payload) => {
    if (!node || typeof node.publishToPeerGroup !== 'function' || !peerGroupJoinedRef.current) {
      return false;
    }
    try {
      if (connectTargets.length && typeof node.joinPeerGroup === 'function') {
        await node.joinPeerGroup(LOBBY_CHAT_PEER_GROUP, {
          bootstrapPeerIds: connectTargets,
          fanout: 4,
          maxActivePeers: 12,
          maxHops: 32
        }).catch(() => undefined);
      }
      await node.publishToPeerGroup(LOBBY_CHAT_PEER_GROUP, type, payload);
      return true;
    } catch {
      return false;
    }
  }, [connectTargets, node]);

  const broadcastChat = useCallback(async (type, payload) => {
    const targets = connectTargets;
    if (targets.length && typeof ensureLinkedToPeerIds === 'function') {
      await ensureLinkedToPeerIds(targets).catch(() => undefined);
    }
    const viaGroup = await publishToPeerGroup(type, payload);
    const result = await deliverChatPayload(node, type, payload, {
      connectToPeers: targets,
      broadcastScope: LOBBY_SCOPE,
      attempts: targets.length ? 3 : 1,
      settleMs: 120,
      maxConnectTargets: MAX_CHAT_CONNECT_TARGETS,
      publishOutOfBand: type === CHAT_MESSAGE_TYPE && typeof publishOutOfBand === 'function'
        ? publishOutOfBand
        : (type === CHAT_ROOM_MESSAGE_TYPE && typeof publishRoomOutOfBand === 'function'
          ? publishRoomOutOfBand
          : null)
    });
    return {
      ...result,
      delivered: Boolean(result.delivered || viaGroup),
      deliveredVia: result.deliveredVia || (viaGroup ? 'peergroup' : '')
    };
  }, [
    connectTargets,
    ensureLinkedToPeerIds,
    node,
    publishOutOfBand,
    publishRoomOutOfBand,
    publishToPeerGroup
  ]);

  // When a PeerJS link opens, flush recent chat/rooms (chess-demo late-join pattern).
  useEffect(() => {
    const openKey = [...(openPeerIds || [])].filter(Boolean).sort().join('|');
    if (!node || !joined || !openKey || openKey === lastOpenKeyRef.current) {
      return;
    }
    lastOpenKeyRef.current = openKey;
    const { rooms: syncRooms, recentChat } = getChatSyncPayload();
    const targets = openKey.split('|');
    void (async () => {
      for (const room of syncRooms) {
        await deliverChatPayload(node, CHAT_ROOM_MESSAGE_TYPE, room, {
          connectToPeers: targets,
          broadcastScope: LOBBY_SCOPE,
          attempts: 1,
          settleMs: 0,
          maxConnectTargets: MAX_CHAT_CONNECT_TARGETS
        }).catch(() => undefined);
        await publishToPeerGroup(CHAT_ROOM_MESSAGE_TYPE, room);
      }
      for (const entry of recentChat.slice(-6)) {
        await deliverChatPayload(node, CHAT_MESSAGE_TYPE, entry, {
          connectToPeers: targets,
          broadcastScope: LOBBY_SCOPE,
          attempts: 1,
          settleMs: 0,
          maxConnectTargets: MAX_CHAT_CONNECT_TARGETS
        }).catch(() => undefined);
        await publishToPeerGroup(CHAT_MESSAGE_TYPE, entry);
      }
    })();
  }, [getChatSyncPayload, joined, node, openPeerIds, publishToPeerGroup]);

  const rememberOutbound = useCallback((sealed) => {
    if (!sealed) {
      return;
    }
    const next = [...pendingOutboundRef.current, sealed]
      .slice(-PENDING_CHAT_HELLO_LIMIT);
    pendingOutboundRef.current = next;
  }, []);

  const getPendingChatForHello = useCallback(() => (
    pendingOutboundRef.current.slice(-PENDING_CHAT_HELLO_LIMIT)
  ), []);

  const getPendingRoomsForHello = useCallback(() => (
    (roomsRef.current || [])
      .filter((room) => room.roomId && room.roomId !== MAIN_CHAT_ROOM_ID)
      .slice(-6)
      .map((room) => ({
        roomId: room.roomId,
        title: room.title,
        createdBy: room.createdBy || '',
        createdAt: room.createdAt || Date.now()
      }))
  ), []);

  const messages = messagesByRoom[activeRoomId] || [];

  const sendMessage = useCallback(async (text) => {
    if (!node || !joined || !identity?.playerId) {
      setError('Join the lobby before chatting.');
      return false;
    }
    setSending(true);
    setError('');
    try {
      const base = {
        roomId: activeRoomIdRef.current,
        text: String(text || '').trim().slice(0, 280),
        nickname,
        playerId: identity.playerId,
        sentAt: Date.now()
      };
      if (!base.text) {
        setError('Message is empty or invalid.');
        return false;
      }

      const sealed = (await sealChatMessage(base)) || base;
      rememberOutbound(sealed);
      const payload = sealed;

      const localBus = publishLocalLobbyEnvelope({
        type: CHAT_MESSAGE_TYPE,
        senderId: identity.peerId,
        payload
      });

      // Show the message immediately — do not block the Send button on PeerJS dials.
      appendChat({
        ...payload,
        senderId: identity.peerId,
        receivedAt: Date.now(),
        messageKey: `${identity.peerId}:${payload.sentAt}:${payload.text.slice(0, 24)}`
      });

      // Background fanout: PeerBasket / PeerGroup / WebRTC (can take many seconds).
      void (async () => {
        try {
          if (connectTargets.length && typeof node.joinDiscovery === 'function') {
            await node.joinDiscovery(LOBBY_SCOPE, {
              bootstrapPeerIds: connectTargets
            }).catch(() => undefined);
          }
          if (connectTargets.length && typeof ensureLinkedToPeerIds === 'function') {
            await ensureLinkedToPeerIds(connectTargets).catch(() => undefined);
          }

          const oobResult = typeof publishOutOfBand === 'function'
            ? await publishOutOfBand(payload).catch(() => ({ published: false, queued: false }))
            : { published: false, queued: false };

          const viaGroup = await publishToPeerGroup(CHAT_MESSAGE_TYPE, payload);
          const openNow = node.getConnectionStats?.().peerIds || [];
          const result = await deliverChatPayload(node, CHAT_MESSAGE_TYPE, payload, {
            // Only spend time dialing when nothing is open yet.
            connectToPeers: openNow.length ? openNow : connectTargets.slice(0, 4),
            broadcastScope: LOBBY_SCOPE,
            attempts: openNow.length ? 1 : 2,
            settleMs: 0,
            maxConnectTargets: 4,
            publishOutOfBand: null
          });

          const delivered = Boolean(
            result.delivered
            || viaGroup
            || oobResult?.published
            || localBus?.published
          );
          const pendingOob = Boolean(!delivered && (oobResult?.queued || oobResult?.advert));
          const deliveredVia = result.deliveredVia
            || (localBus?.published ? 'local-bus' : '')
            || (viaGroup ? 'peergroup' : '')
            || (oobResult?.published ? 'peerbasket' : '')
            || (pendingOob ? 'peerbasket-queued' : '');

          lobbyDebug('chat-send', {
            text: payload.text,
            connectTargets,
            delivered,
            pendingOob,
            deliveredVia,
            viaGroup,
            localBus: Boolean(localBus?.published),
            oobPublished: oobResult?.published,
            oobQueued: oobResult?.queued,
            oobReason: oobResult?.reason,
            openPeerIds: result.openPeerIds,
            selfPeerId: identity.peerId,
            selfPlayerId: identity.playerId,
            roomId: payload.roomId,
            async: true
          });
          lobbyDebugReport('CHAT_SEND', {
            role: 'sender',
            text: payload.text,
            roomId: payload.roomId,
            selfPeerId: identity.peerId,
            selfPlayerId: identity.playerId,
            delivered,
            deliveredVia,
            pendingOob,
            connectTargets,
            openPeerIds: result.openPeerIds,
            localBus: Boolean(localBus?.published),
            viaGroup: Boolean(viaGroup),
            oobPublished: Boolean(oobResult?.published),
            oobQueued: Boolean(oobResult?.queued),
            oobReason: oobResult?.reason || '',
            hint: delivered
              ? 'Sender thinks message left this tab. Check the OTHER browser for CHAT_RECV / chat-ingest-* lines.'
              : 'Sender could not confirm delivery. Expect PeerBasket budget/CORS or zero open WebRTC peers.'
          });

          if (typeof onChatSent === 'function') {
            void onChatSent({
              sealed: payload,
              delivered,
              deliveredVia
            });
          }
          if (!delivered) {
            setError(pendingOob
              ? 'Message queued for PeerBasket — keep Play open; peer may see it within ~20s.'
              : 'Could not reach peers yet. Stay on Play — chat syncs when a link opens.');
          }
        } catch (bgError) {
          lobbyDebug('chat-send-bg-error', {
            message: bgError?.message || String(bgError),
            text: payload.text
          });
        }
      })();

      return true;
    } catch (sendError) {
      setError(sendError.message || 'Failed to send chat message.');
      return false;
    } finally {
      setSending(false);
    }
  }, [
    appendChat,
    connectTargets,
    ensureLinkedToPeerIds,
    identity,
    joined,
    nickname,
    node,
    onChatSent,
    publishOutOfBand,
    publishToPeerGroup,
    rememberOutbound
  ]);

  const createRoom = useCallback(async (title) => {
    if (!node || !joined || !identity?.playerId) {
      setError('Join the lobby before creating a room.');
      return null;
    }
    setError('');
    try {
      const roomId = normalizeRoomId(title);
      const sealed = await sealChatRoom({
        roomId,
        title,
        createdBy: identity.playerId
      });
      if (!sealed) {
        setError('Choose a short alphanumeric room name.');
        return null;
      }
      publishLocalLobbyEnvelope({
        type: CHAT_ROOM_MESSAGE_TYPE,
        senderId: identity.peerId,
        payload: sealed
      });
      setRooms((current) => mergeChatRooms(current, sealed));
      setActiveRoomId(sealed.roomId);
      // Same as chat send: show the room now. Mesh fanout can take many seconds.
      void broadcastChat(CHAT_ROOM_MESSAGE_TYPE, sealed).catch(() => undefined);
      return sealed;
    } catch (createError) {
      setError(createError.message || 'Failed to create room.');
      return null;
    }
  }, [broadcastChat, identity, joined, node]);

  const selectRoom = useCallback((roomId) => {
    const id = normalizeRoomId(roomId) || MAIN_CHAT_ROOM_ID;
    setActiveRoomId(id);
    setRooms((current) => mergeChatRooms(current, {
      roomId: id,
      title: id === MAIN_CHAT_ROOM_ID ? 'Main Room' : id,
      createdBy: '',
      createdAt: Date.now()
    }));
  }, []);

  return {
    activeRoomId,
    rooms,
    messages,
    sending,
    error,
    sendMessage,
    createRoom,
    selectRoom,
    setError,
    getPendingChatForHello,
    getPendingRoomsForHello,
    getChatSyncPayload
  };
}
